import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Banknote, CreditCard, X } from 'lucide-react';

/**
 * Cash-vs-Card picker rendered when a barber / shop admin marks a
 * booking as completed and there's still an outstanding balance owing.
 * Called by both the Diary and Dashboard so the flow is identical
 * everywhere.
 *
 * Props:
 *   • outstanding : number — how much the customer owes (£)
 *   • customerName, serviceName — for the sheet header
 *   • onPick(method) — 'cash' | 'card' - caller does the POST + marks complete
 *   • onSkip()       — user picks "Skip — still owing" - caller marks complete
 *                       WITHOUT clearing the balance (booking stays partially paid)
 *   • onClose()      — dismiss without completing anything
 *
 * Mounted controlled — parent decides when it's open via `active`.
 */
export const PaymentActionSheet = ({
  active,
  outstanding,
  customerName,
  serviceName,
  onPick,
  onSkip,
  onClose,
  busy = false,
  header = 'Complete booking',
  hint = 'How was the balance collected at the chair?',
  skipLabel = null,
  skipHint = null,
}) => {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [active, onClose]);

  if (!active) return null;

  const amt = Number(outstanding || 0).toFixed(2);

  // Portal the sheet + backdrop to `document.body` so their `position:
  // fixed` truly escapes any ancestor stacking context (e.g. `.side`'s
  // z-index: 30 on mobile). Matches the pattern StatusMenu already uses
  // in Diary.js.
  return createPortal(
    <>
      <div className="pas-backdrop" onClick={onClose} data-testid="pay-sheet-backdrop" />
      <div className="pas-sheet" role="dialog" aria-modal="true" data-testid="pay-sheet">
        <div className="pas-grip" aria-hidden="true" />
        <button
          type="button"
          className="pas-close"
          onClick={onClose}
          aria-label="Cancel"
          data-testid="pay-sheet-close"
        >
          <X size={17} />
        </button>

        <div className="pas-header">{header}</div>
        <div className="pas-sub">
          {customerName} · {serviceName}
        </div>
        <div className="pas-amt">
          £{amt}
          <span className="pas-amt-tag">outstanding</span>
        </div>

        <div className="pas-hint">
          {hint}
        </div>

        <button
          type="button"
          className="pas-action"
          disabled={busy}
          onClick={() => onPick('cash')}
          data-testid="pay-sheet-cash"
        >
          <Banknote size={18} />
          <span>
            <b>Cash</b>
            <br />
            <small>Paid in person &mdash; logged for the till</small>
          </span>
        </button>

        <button
          type="button"
          className="pas-action"
          disabled={busy}
          onClick={() => onPick('card')}
          data-testid="pay-sheet-card"
        >
          <CreditCard size={18} />
          <span>
            <b>Card</b>
            <br />
            <small>Terminal, Sumup, or any card reader</small>
          </span>
        </button>

        {onSkip && (
        <button
          type="button"
          className="pas-action pas-skip"
          disabled={busy}
          onClick={onSkip}
          data-testid="pay-sheet-skip"
        >
          <span>
            <b>{skipLabel || 'Not yet — still owing'}</b>
            <br />
            <small>{skipHint || 'Mark complete but leave the balance outstanding'}</small>
          </span>
        </button>
        )}
      </div>
    </>,
    document.body
  );
};

export default PaymentActionSheet;
